import isEqual from "lodash.isequal";

import { v4 } from "uuid";
import { sharedFromAiFormat } from "@src/store/slices/experienceBuilder/common/utils/sharedFromAiFormat";
import { pickSharedFields } from "@src/store/slices/experienceBuilder/common/utils/pickSharedFields";
import {
  LayoutMod,
  ModHandler,
} from "@src/store/slices/experienceBuilder/types";
import { isValidSelector } from "@src/store/slices/experienceBuilder/common/utils/html/isValidSelector";

export const layoutModHandler: ModHandler<LayoutMod> = {
  normalize: (mod) => ({
    ...mod,
    selector: mod.selector.trim(),
  }),

  convertTo: (conversion) => {
    if (conversion.type !== "onsiteEdit")
      throw new Error("Expected onsiteEdit");

    const edit = conversion.dto;
    const selector = edit.querySelectors.join(",");
    return {
      id: edit.id,
      category: "basicMod",
      type: "layout",
      groupId: edit.groupId,
      selector,
      description: edit.title ?? undefined,
      html: edit.replace,
      remove: edit.remove,
      insertType: edit.insertType ?? "replace",
      variationId: edit.variationId,
      nodes: [],
      createdAtUrl: edit.createdAtUrl ?? undefined,
      source: edit.source ?? undefined,
    };
  },

  convertFrom: (mod) => ({
    type: "onsiteEdit",
    dto: {
      id: mod.id,
      groupId: mod.groupId,
      variationId: mod.variationId,
      replace: mod.html,
      find: null,
      querySelectors: [mod.selector],
      isRegex: false,
      skip: false,
      remove: mod.remove,
      isHtml: false,
      isImage: false,
      onsiteEditType: "html",
      insertType: mod.insertType === "replace" ? null : mod.insertType,
      createdAtUrl: mod.createdAtUrl,
      source: mod.source ?? null,
      title: mod.description ?? null,
    },
  }),

  getErrors: (mod) => {
    const errors: Record<string, string> = {};

    if (!mod.selector) {
      errors.selector = "Element selection is required";
    }
    if (mod.selector && !isValidSelector(mod.selector)) {
      errors.selector = "Selector is invalid";
    }
    if (!mod.html && !mod.remove) {
      errors.html = "HTML or hide element is required";
      errors.remove = "HTML or hide element is required";
    }
    return errors;
  },

  isEquivalent: (a, b) => b.type === "layout" && a.selector === b.selector,

  defaults: () => ({
    id: v4(),
    type: "layout",
    category: "basicMod",
    variationId: "",
    selector: "",
    html: "",
    remove: false,
    insertType: "replace",
    nodes: [],
  }),

  // `layout` is real, live merchant data (raw HTML mods created in the visual
  // builder) but has no AI-authoring schema — the AI never learns "layout"
  // exists as a type, so it can't create new ones. Presented here as `text`
  // (editMode: "html") instead: structurally the same edit at the
  // DOM-patching layer (both convert to an `onsiteEditType: "html"` DTO, see
  // `convertFrom`), so the AI can fully see and edit it using a shape it
  // already knows. On write-back, `aiModsToFullFormat` resolves the
  // existing-by-id `LayoutMod` and routes the AI's `type: "text"` payload
  // through `textModHandler.fromAiFormat` — the mod becomes a real `text`
  // mod the first time the AI touches it. Untouched `layout` mods are never
  // sent back (merge/delete only round-trips what's actually edited), so in
  // practice most stay `layout` in storage indefinitely.
  toAiFormat: (mod) => ({
    id: mod.id,
    type: "text",
    description: mod.description,
    editMode: "html",
    selector: mod.selector,
    find: "",
    replace: "",
    html: mod.html,
    remove: mod.remove,
    insertType: mod.insertType,
  }),

  fromAiFormat: (aiMod, context) => ({
    ...(context.existing ?? layoutModHandler.defaults()),
    ...aiMod,
    ...sharedFromAiFormat(aiMod, context),
  }),

  hasChanges: (oldMod, newMod) => {
    const normalize = (mod: LayoutMod) => ({
      description: mod.description,
      selector: mod.selector,
      html: mod.html,
      remove: mod.remove,
      insertType: mod.insertType,
    });
    return !isEqual(normalize(oldMod), normalize(newMod));
  },
};
